import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DueDateItem {
  userId: string;
  courseId: string;
  courseTitle: string;
  userName: string;
  userEmail: string;
  organizationName: string;
  enrollmentDeadline: string | null;
  completionDueDate: string | null;
  assignedAt: string;
  status: string;
  dueStatus: 'no_due_date' | 'overdue' | 'due_soon' | 'due_this_week' | 'on_track';
  daysUntilDue: number | null;
  isCompleted: boolean;
}

export interface DueDateSummary {
  total: number;
  overdue: number;
  dueSoon: number;
  dueThisWeek: number;
  onTrack: number;
  noDueDate: number;
}

export interface AssignCourseRequest {
  userIds: string[];
  enrollmentDeadline?: string;
  completionDueDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DueDatesService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Get all due dates for the current user (learners see their own, instructors see all)
  getDueDates(limit?: number): Observable<{ dueDates: DueDateItem[] }> {
    const params = limit ? `?limit=${limit}` : '';
    return this.http.get<{ dueDates: DueDateItem[] }>(`${this.apiUrl}/due-dates${params}`);
  }

  // Get due date summary statistics
  getDueDateSummary(): Observable<DueDateSummary> {
    return this.http.get<DueDateSummary>(`${this.apiUrl}/due-dates/summary`);
  }

  // Get course assignments for a specific course (instructors only)
  getCourseAssignments(courseId: string): Observable<{ assignments: DueDateItem[] }> {
    return this.http.get<{ assignments: DueDateItem[] }>(`${this.apiUrl}/courses/${courseId}/assignments`);
  }

  // Assign course to users with due dates (instructors only)
  assignCourse(courseId: string, assignment: AssignCourseRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/courses/${courseId}/assign`, assignment);
  }

  // Extend due date for a user (instructors only)
  extendDueDate(courseId: string, userId: string, newDueDate: string, reason?: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/courses/${courseId}/extend-due-date`, {
      userId,
      newDueDate,
      reason
    });
  }

  // Helper function to get due status color class
  getDueStatusClass(dueStatus: string): string {
    switch (dueStatus) {
      case 'overdue':
        return 'text-error';
      case 'due_soon':
        return 'text-warning';
      case 'due_this_week':
        return 'text-info';
      case 'on_track':
        return 'text-success';
      default:
        return 'text-base-content';
    }
  }

  // Helper function to get due status badge class
  getDueStatusBadgeClass(dueStatus: string): string {
    switch (dueStatus) {
      case 'overdue':
        return 'badge-error';
      case 'due_soon':
        return 'badge-warning';
      case 'due_this_week':
        return 'badge-info';
      case 'on_track':
        return 'badge-success';
      default:
        return 'badge-ghost';
    }
  }

  // Helper function to format due status text
  getDueStatusText(dueStatus: string, daysUntilDue: number | null): string {
    switch (dueStatus) {
      case 'overdue':
        const daysPast = daysUntilDue ? Math.abs(daysUntilDue) : 0;
        return `${daysPast} day${daysPast !== 1 ? 's' : ''} overdue`;
      case 'due_soon':
        return daysUntilDue === 1 ? 'Due tomorrow' : `Due in ${daysUntilDue} days`;
      case 'due_this_week':
        return `Due in ${daysUntilDue} days`;
      case 'on_track':
        return daysUntilDue ? `${daysUntilDue} days remaining` : 'On track';
      case 'no_due_date':
        return 'No due date';
      default:
        return 'Unknown status';
    }
  }

  // Format date for display
  formatDate(dateString: string | null): string {
    if (!dateString) return 'No date set';
    
    const date = new Date(dateString);
    const now = new Date();
    // Normalize both dates to midnight to compare calendar days, not raw time
    const dateNorm = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowNorm = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((dateNorm.getTime() - nowNorm.getTime()) / (1000 * 60 * 60 * 24));
    
    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
    
    if (diffDays === 0) {
      return `${formatted} (Today)`;
    } else if (diffDays === 1) {
      return `${formatted} (Tomorrow)`;
    } else if (diffDays === -1) {
      return `${formatted} (Yesterday)`;
    } else if (diffDays < 0) {
      return `${formatted} (${Math.abs(diffDays)} days ago)`;
    } else if (diffDays <= 7) {
      return `${formatted} (In ${diffDays} days)`;
    }
    
    return formatted;
  }
}